import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import VideoPlayer from "@/pages/live/components/VideoPlayer";
import ScoreBoard from "@/pages/live/components/ScoreBoard";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  getEventById,
  getRelatedEvents,
  sportNames,
  type SportKey,
} from "@/mocks/events";

const posterImages: Record<SportKey, string> = {
  basketball:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20indoor%20basketball%20arena%20with%20warm%20orange%20spotlights%20and%20silhouetted%20crowd%2C%20deep%20black%20shadows%2C%20atmospheric%20haze%2C%20broadcast%20camera%20view%2C%20high%20contrast%2C%20no%20text&width=1280&height=720&seq=jrs-player-basketball&orientation=landscape",
  football:
    "https://readdy.ai/api/search-image?query=Dark%20cinematic%20floodlit%20football%20pitch%20at%20night%20with%20warm%20orange%20rim%20light%20and%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20broadcast%20camera%20view%2C%20high%20contrast%2C%20no%20text&width=1280&height=720&seq=jrs-player-football&orientation=landscape",
  esports:
    "https://readdy.ai/api/search-image?query=Dark%20futuristic%20esports%20stage%20with%20orange%20and%20red%20neon%20beams%20and%20giant%20glowing%20screens%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20broadcast%20camera%20view%2C%20high%20contrast%2C%20no%20text&width=1280&height=720&seq=jrs-player-esports&orientation=landscape",
};

const infoRows = (name: string) => name;

export default function LiveDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const event = eventId ? getEventById(eventId) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  usePageMeta(
    event ? `${event.title}直播 - jrs直播` : "赛事直播 - jrs直播",
    event
      ? `${event.title}将于${event.time}进行，${event.league}${event.round}，${event.home.name}对阵${event.away.name}，jrs直播提供实时比分与直播入口。`
      : "在 jrs直播观看精彩的体育赛事直播。",
    event
      ? `${event.title},${event.home.name},${event.away.name},${event.league},${sportNames[event.sport]}直播,jrs直播`
      : "jrs直播,赛事直播",
  );

  if (!event) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-[720px] flex-col items-center justify-center px-4 py-20 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          赛事不存在或已下线
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的赛事可能已经结束或链接有误，返回赛程页查看当前可观看的赛事。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/schedule"
            className="rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            查看全部赛程
          </Link>
          <Link
            to="/"
            className="rounded-md border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedEvents(event);
  const favorited = isFavorite(event.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  const detailInfo = [
    { icon: "ri-trophy-line", label: "赛事等级", value: event.level },
    { icon: "ri-calendar-event-line", label: "比赛时间", value: event.time },
    { icon: "ri-map-pin-2-line", label: "比赛地点", value: event.venue },
    { icon: "ri-earth-line", label: "所属赛区", value: event.region },
    { icon: "ri-stack-line", label: "赛事阶段", value: event.round },
    {
      icon: "ri-group-line",
      label: "参赛信息",
      value: `${infoRows(event.home.name)} vs ${event.away.name}`,
    },
  ];

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-12">
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category/${event.sport}`}
          className="transition-colors hover:text-primary-400"
        >
          {sportNames[event.sport]}直播
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{event.title}</span>
      </nav>

      <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={event.status} />
            <span className="text-xs text-foreground-500">
              {event.league} · {event.round}
            </span>
          </div>
          <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
            {event.title}直播
          </h1>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <TeamBadge team={event.home} size={34} />
              <span className="text-sm font-medium text-foreground-800">
                {event.home.name}
              </span>
            </div>
            <span className="font-heading text-sm font-bold text-foreground-500">VS</span>
            <div className="flex items-center gap-2">
              <TeamBadge team={event.away} size={34} />
              <span className="text-sm font-medium text-foreground-800">
                {event.away.name}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => toggleFavorite(event.id)}
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
              favorited
                ? "border-primary-500 bg-primary-500/15 text-primary-400"
                : "border-background-300 bg-background-100 text-foreground-800 hover:border-primary-500/50 hover:text-primary-400"
            }`}
            aria-pressed={favorited}
          >
            <i className={favorited ? "ri-star-fill" : "ri-star-line"} />
            {favorited ? "已收藏" : "收藏赛事"}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className={copied ? "ri-check-line text-primary-400" : "ri-share-line"} />
            {copied ? "链接已复制" : "分享"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col gap-6">
          <VideoPlayer
            poster={posterImages[event.sport]}
            title={event.title}
            isLive={event.status === "live"}
          />

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              赛事简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {event.description}
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {detailInfo.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] text-foreground-500">
                      {item.label}
                    </div>
                    <div className="truncate text-sm font-medium text-foreground-900">
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <ScoreBoard event={event} />

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              相关赛事推荐
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              同项目或相关球队的近期赛事
            </p>
            <div className="mt-4 flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/live/${item.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-500/50 cursor-pointer"
                >
                  <div className="flex flex-col items-center gap-1">
                    <TeamBadge team={item.home} size={30} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-400">
                      {item.title}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-500">
                      <span>{item.league}</span>
                      <span>·</span>
                      <span>{item.time}</span>
                    </div>
                  </div>
                  {item.status !== "upcoming" ? (
                    <span className="font-heading text-sm font-bold text-primary-400">
                      {item.homeScore}:{item.awayScore}
                    </span>
                  ) : (
                    <span className="text-xs text-foreground-500">预告</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">
            更多{sportNames[event.sport]}赛事
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.slice(0, 3).map((item) => (
              <EventCard key={item.id} event={item} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}